import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { RouteComponentProps } from 'react-router';
import { useHistory, useParams, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { StringParam, useQueryParam } from 'use-query-params';
import {
  Box,
  Button,
  ButtonGroup,
  Fab,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Zoom
} from '@material-ui/core';
import {
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  CloudUploadOutlined as CloudUploadOutlinedIcon,
  Folder as FolderIcon,
  Forward as ForwardIcon,
  MoreHoriz as MoreHorizIcon,
  ViewColumn as ViewColumnIcon,
  ViewList as ViewListIcon
} from '@material-ui/icons';

import { ApiParams, FetchError } from '../../common/api/api';
import AppSnackbar, { AppSnackbarProps } from '../../common/AppSnackbar';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateBodyCard from '../../common/page-template/PageTemplateBodyCard';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader from '../../common/page-template/PageTemplateHeader';
import SearchBox from '../../common/SearchBox';
import { useUserContext } from '../../user/User.context';
import TaskBulkTransitionListItems from '../tasks/components/TaskBulkTransitionListItems';
import TaskBulkActionDialog from '../tasks/dialogs/TaskBulkActionDialog';
import { useGroupTaskBulkEvents } from '../tasks/Task.hooks';
import { TaskBulkEvent, TaskBulkEventGroup } from '../tasks/Task.interface';
import DocumentBoardFilter from './components/DocumentBoardFilter';
import DocumentBreadcrumbs from './components/DocumentBreadcrumbs';
import DocumentFileExplorerDialog from './components/DocumentFileExplorerDialog';
import DocumentFolderUpload, {
  FileUploadProgress
} from './components/DocumentFolderUpload';
import DocumentListExportButton from './components/DocumentListExportButton';
import DocumentMultiUpload from './components/DocumentMultiUpload';
import { FileExplorerContext } from './components/FileExplorer.context';
import {
  useDocumentFilterQueryParams,
  useFindDocuments,
  useSearchFiles
} from './Document.hook';
import { FileDto } from './Document.interface';
import DocumentCreateFolderDialog from './DocumentCreateFolderDialog';
import DocumentInfoDrawer from './DocumentInfoDrawer';
import DocumentList from './DocumentList';
import DocumentTaskBoard from './DocumentTaskBoard';

enum ViewEnum {
  List = 'list',
  Board = 'board'
}

const getFolderIdFromPathname = (pathname: string): number => {
  const parsedPath = pathname.substr(pathname.indexOf('folder/'));
  const folderId: string | undefined = parsedPath.split('/')[1];
  // use -1 (root directory) as default if no folder selected
  return folderId ? parseInt(folderId) : -1;
};

const StyledContainer = styled.div`
  flex: 1;
  display: flex;
  width: 100%;
  overflow: hidden;
  position: relative;
`;

const StyledFab = styled(Fab)`
  position: absolute;
  top: 82px;
  left: 26px;
  z-index: 3;
`;

const StyledTitle = styled.div`
  display: flex;
  flex-direction: column;
`;

const zoomTransitionProps = { in: true, timeout: 300 };

const DocumentListPage: React.FC<RouteComponentProps> = (
  props: RouteComponentProps
) => {
  const { location } = props;
  const history = useHistory();
  const { folderId: paramFolderId } = useParams<{ folderId?: string }>();
  const folderId = paramFolderId ? parseInt(paramFolderId) : undefined;
  const [view, setView] = useQueryParam('view', StringParam);
  const [search = '', setSearch] = useQueryParam('search', StringParam);
  const filter = useDocumentFilterQueryParams();
  const [dialog, setDialog] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    severity: AppSnackbarProps['severity'];
    message?: React.ReactNode;
  }>({ open: false, severity: 'success' });
  const [fileList, setFileList] = useState<FileDto[]>([]);
  const [bulkEventGroups, setBulkEventGroups] = useState<TaskBulkEventGroup[]>(
    []
  );
  const [selectedBulkEvent, setSelectedBulkEvent] = useState<TaskBulkEvent>();
  // save file ids
  const [selectedIDs, setSelectedIDs] = useState<number[]>([]);
  const [{ data, isLoading: isFinding, error: findError }, apiFindDocuments] =
    useFindDocuments(folderId);
  const groupBulkEvents = useGroupTaskBulkEvents();
  const [
    { data: searchedFiles, isLoading: isSearching, error: searchError },
    searchFiles
  ] = useSearchFiles();
  const {
    refresh: navRefresh,
    // selected: navSelected,
    // setSelected: setNavSelected,
    refreshNode: navRefreshNode
  } = useContext(FileExplorerContext);
  const hasSelected = !!selectedIDs.length;
  const hasFilter = !!(filter.options || search);
  const isLoading: boolean = isFinding || isSearching;
  const error: FetchError | undefined = findError || searchError;
  const currFolderId: string = `${getFolderIdFromPathname(location.pathname)}`;
  const can = useUserContext().permissions.file;

  const apiParams = useMemo(() => {
    // add search propery to params (used for board view)
    const params: ApiParams = { ...filter.options };
    if (search) params.search = search;
    return params;
  }, [filter.options, search]);

  const filesForTransition = useMemo(() => {
    return fileList.filter(file => {
      return (
        !file.isFolder &&
        file.task &&
        (selectedIDs.length === 0 || selectedIDs.includes(file.id))
      );
    });
  }, [fileList, selectedIDs]);

  useEffect(() => {
    !hasFilter && data && setFileList(data.children || []);
  }, [data, hasFilter]);

  useEffect(() => {
    hasFilter && searchedFiles && setFileList(searchedFiles);
  }, [hasFilter, searchedFiles]);

  useEffect(() => {
    if (error) {
      setSnackbar({
        open: true,
        severity: 'error',
        message:
          error.message ||
          'There was an error getting data from the server. Please check your internet connection.'
      });
    }
  }, [error]);

  useEffect(() => {
    const run = async () => {
      const groups =
        filesForTransition.length > 0
          ? await groupBulkEvents(filesForTransition)
          : [];
      setBulkEventGroups(groups);
    };
    run();
  }, [groupBulkEvents, filesForTransition]);

  const findDocuments = useCallback(
    (params?: ApiParams) => {
      // refetch files when filtering
      hasFilter && searchFiles({ ...filter.options, search });
      // search current folder if not yet fetched
      if (!data || !hasFilter) {
        apiFindDocuments(params);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search, filter.options, hasFilter, searchFiles, apiFindDocuments]
  );

  const refreshList = () => {
    // reset selected files and refetch
    setFileList([]);
    setSelectedIDs([]);
    setSelectedDocument(undefined);
    findDocuments({ withChildren: true });
  };

  useEffect(() => {
    // refresh list on page change
    refreshList();
    // change selected on nav treeview
    // setNavSelected(currFolderId);
    // reset it on unmount
    // return () => setNavSelected(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [isDocumentDrawerOpen, setIsDocumentDrawerOpen] = useState(false);
  const [isOpenMultiUploadDialog, setIsOpenMultiUploadDialog] =
    useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<FileDto>();
  const [driveMenuAnchor, setDriveMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const folderInputRef = useRef<HTMLInputElement | null>(null);

  const handleRowDoubleClick = (file: FileDto) => {
    if (file.isFolder) {
      // clear search to show files within folder instead of searched files
      search && setSearch('');
      history.push(`/portal/documents/folder/${file.id}`);
    } else {
      history.push(`/portal/documents/file/task/${file.taskId || 0}`);
    }
  };

  const handleRowSingleClick = (file: FileDto) => {
    if (!selectedDocument || file.id !== selectedDocument.id) {
      setSelectedDocument(file);
      setIsDocumentDrawerOpen(true);
    } else {
      setSelectedDocument(undefined);
      setIsDocumentDrawerOpen(false);
    }
  };

  const handleDrawerClose = () => {
    setSelectedDocument(undefined);
    setIsDocumentDrawerOpen(false);
  };

  const handleProgressCount = (
    progress: FileUploadProgress,
    shouldFetch: boolean = true
  ) => {
    const { success } = progress;
    if (success > 0) {
      setSnackbar({
        open: true,
        severity: 'success',
        message: `Number of files uploaded: ${success}`
      });
      shouldFetch && findDocuments({ withChildren: true });
    }
  };

  return (
    <StyledContainer>
      {view !== ViewEnum.Board && (
        <StyledFab
          color='primary'
          onClick={e => setDriveMenuAnchor(e.currentTarget)}
        >
          {/* ternary doesn't work for the transitions */}
          {hasSelected && (
            <Zoom {...zoomTransitionProps}>
              <MoreHorizIcon />
            </Zoom>
          )}
          {!hasSelected && (
            <Zoom {...zoomTransitionProps}>
              <AddIcon />
            </Zoom>
          )}
        </StyledFab>
      )}

      <Menu
        keepMounted
        id='drive-menu'
        anchorEl={driveMenuAnchor}
        open={Boolean(driveMenuAnchor)}
        onClose={() => setDriveMenuAnchor(null)}
      >
        {hasSelected && (
          <MenuItem
            disabled={can.not.update}
            onClick={() => {
              setDialog(true);
              setDriveMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <ForwardIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText>Move Files</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          disabled={can.not.create}
          onClick={() => {
            setIsOpenDialog(true);
            setDriveMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <FolderIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText>New Folder</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={can.not.create}
          onClick={() => {
            setIsOpenMultiUploadDialog(true);
            setDriveMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <CloudUploadIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText>Upload Files</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={can.not.create}
          onClick={() => {
            folderInputRef.current?.click();
            setDriveMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <CloudUploadOutlinedIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText>Upload Folder</ListItemText>
        </MenuItem>
        {bulkEventGroups.length > 0 && (
          <TaskBulkTransitionListItems
            bulkEventGroups={bulkEventGroups}
            onSelect={bulkEvent => {
              setSelectedBulkEvent(bulkEvent);
              setDriveMenuAnchor(null);
            }}
          />
        )}
      </Menu>
      {selectedBulkEvent && (
        <TaskBulkActionDialog
          open
          bulkEvent={selectedBulkEvent}
          getTitle={taskEntity => (taskEntity as FileDto).name}
          onClose={processedEntities => {
            setSelectedBulkEvent(undefined);
            processedEntities && findDocuments({ withChildren: true });
          }}
        />
      )}
      {data && (
        <DocumentMultiUpload
          folder={data}
          open={isOpenMultiUploadDialog}
          onDismiss={handleProgressCount}
          setOpen={setIsOpenMultiUploadDialog}
        />
      )}
      <DocumentFolderUpload
        parentFolder={data}
        folderInputRef={folderInputRef}
        onCreateFolder={() => findDocuments({ withChildren: true })}
        onDismiss={(progress: FileUploadProgress, folder?: FileDto) => {
          handleProgressCount(progress, folder === data);
        }}
      />
      {isOpenDialog && (
        <DocumentCreateFolderDialog
          folderDocumentId={folderId}
          open={isOpenDialog}
          onCancel={() => setIsOpenDialog(false)}
          onCreated={(folder: FileDto) => {
            setIsOpenDialog(false);
            setDriveMenuAnchor(null);
            findDocuments({ withChildren: true });
            setSnackbar({
              open: true,
              severity: 'success',
              message: `New Folder ${folder.name} created.`
            });
          }}
        />
      )}
      <PageTemplate>
        <PageTemplateHeader>
          <StyledTitle>
            {view !== ViewEnum.Board && !!data?.id && (
              <DocumentBreadcrumbs
                documentId={data.id}
                root={{ label: 'Documents', path: '/portal/documents' }}
              />
            )}
            <h2>
              {view !== ViewEnum.Board && data?.name ? data.name : 'Documents'}
            </h2>
          </StyledTitle>
          <Box ml={2}>
            <ButtonGroup variant='contained' color='primary'>
              <Button size='small' onClick={() => setView(ViewEnum.List)}>
                <ViewListIcon />
              </Button>
              <Button size='small' onClick={() => setView(ViewEnum.Board)}>
                <ViewColumnIcon />
              </Button>
            </ButtonGroup>
          </Box>
          <SearchBox value={search} onSearch={setSearch}>
            <DocumentBoardFilter
              options={filter.options}
              onFilter={options => filter.update(options)}
            />
          </SearchBox>
          {view !== ViewEnum.Board && (
            <DocumentListExportButton
              folderName={data?.name || 'Documents'}
              folderId={folderId}
              fileIds={selectedIDs}
              onDownload={() => {
                setSnackbar({
                  open: true,
                  severity: 'info',
                  message:
                    'Downloading files. Please check your browser downloads.'
                });
              }}
            />
          )}
        </PageTemplateHeader>
        {view === ViewEnum.Board ? (
          <PageTemplateDashboard>
            <DocumentTaskBoard
              apiParams={apiParams}
              disabled={can.not.update}
            />
          </PageTemplateDashboard>
        ) : (
          <PageTemplateBodyCard>
            <DocumentList
              files={fileList}
              selectedIDs={selectedIDs}
              onRowDoubleClick={handleRowDoubleClick}
              onRowClick={handleRowSingleClick}
              onSelectionChange={(selection: number[]) =>
                setSelectedIDs(selection)
              }
              isLoading={isLoading}
            />
            <DocumentFileExplorerDialog
              open={dialog}
              filesToMove={selectedIDs}
              onMoveSuccess={() => {
                refreshList();
                navRefresh?.();
                navRefreshNode(currFolderId);
              }}
              onCancel={() => setDialog(false)}
            />
          </PageTemplateBodyCard>
        )}
      </PageTemplate>
      {selectedDocument && (
        <DocumentInfoDrawer
          open={isDocumentDrawerOpen}
          onSuccess={(message: string) => {
            findDocuments({ withChildren: true });
            setSnackbar({ open: true, severity: 'success', message });
          }}
          document={selectedDocument}
          onClose={handleDrawerClose}
          onError={message =>
            setSnackbar({ open: true, severity: 'error', message })
          }
          onDocumentMove={(open, selectedIDs) => {
            setDialog(open);
            setSelectedIDs(selectedIDs);
          }}
          onChange={updatedFile => {
            const label = selectedDocument.isFolder ? 'Folder' : 'Document';
            setSnackbar({
              open: true,
              severity: 'success',
              message: `${label} details saved.`
            });
            // update file instead of refetching all files
            setSelectedDocument(selected => {
              return selected && selected.id === updatedFile.id
                ? { ...selected, ...updatedFile }
                : selected;
            });
            setFileList(files => {
              const index = files.findIndex(file => file.id === updatedFile.id);
              if (index < 0) {
                return files;
              }
              const copy = Array.from(files);
              copy[index] = { ...files[index], ...updatedFile };
              return copy;
            });
          }}
        />
      )}
      <AppSnackbar
        open={snackbar.open}
        severity={snackbar.severity}
        onClose={() => setSnackbar(snackbar => ({ ...snackbar, open: false }))}
      >
        {snackbar.message}
      </AppSnackbar>
    </StyledContainer>
  );
};

export default withRouter(DocumentListPage);
